import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from '../components/Layout';
import UserDetails from '../components/UserDetails';
import { getUserWithPhotoDetails } from '../services/users';

const UserDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if we came from a chat page
  const [cameFromChat, setCameFromChat] = useState(false);
  const [returnChatId, setReturnChatId] = useState(null);
  const [returnToUserId, setReturnToUserId] = useState(null);
  
  useEffect(() => {
    // Check if there's a chat ID stored in session storage
    const chatId = sessionStorage.getItem('returnToChatId');
    const chatOwnerId = sessionStorage.getItem('returnToUserChats');
    
    if (chatId && chatOwnerId) {
      setCameFromChat(true);
      setReturnChatId(chatId);
      setReturnToUserId(chatOwnerId);
    }
  }, []);
  
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError('');
      
      try {
        const userData = await getUserWithPhotoDetails(userId);
        if (userData) {
          setUser(userData);
        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserDetails();
  }, [userId]);
  
  // Handle user update
  const handleUserUpdated = (updatedUser) => {
    setUser(updatedUser);
  };
  
  // Handle user deletion
  const handleUserDeleted = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h5" color="error">Error</Typography>
            <Typography variant="body1">{error}</Typography>
            <Button 
              variant="contained" 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate(-1)} // Go back in history
              sx={{ mt: 2 }}
            >
              Back
            </Button>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 3, mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </Link>
            {cameFromChat && (
              <Link
                underline="hover"
                color="inherit"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(-1)} // Just go back in history
              >
                Chats
              </Link>
            )}
            <Typography color="text.primary">User Details</Typography>
          </Breadcrumbs>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {cameFromChat ? (
              <Button 
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)} // Just go back in history
                sx={{ mr: 2 }}
              >
                Back
              </Button>
            ) : (
              <Button 
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)} // Navigate back in browser history
                sx={{ mr: 2 }}
              >
                Back
              </Button>
            )}
          </Box>
        </Box>

        {user && (
          <UserDetails 
            user={user} 
            onUserUpdated={handleUserUpdated}
            onUserDeleted={handleUserDeleted}
          />
        )}
      </Container>
    </Layout>
  );
};

export default UserDetailsPage;